import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Link as JoyLink,
  Sheet,
  Stack,
  Table,
  Typography,
  Alert,
  LinearProgress,
} from "@mui/joy";

import Page from "../../../components/Pages/Page";
import PageHeader from "../../../components/PageHeader";
import Loading from "../../../components/Loading";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import type { FilterInterface } from "./SearchFilters";
import { FilterRailLayout, RAIL_PAGE_SX } from "./FilterRail";
import { VC_PRIMARY_FACETS } from "./filterFacets";
import { decodeFilterDoc, encodeFilterDoc } from "./filterHydration";
import {
  buildFilterDoc,
  filterValuesFromDoc,
  type FilterValues,
} from "./filterState";
import { ActiveFilters } from "./ActiveFilters";
import { useBKGVCIndex, useBKGVCExport, useBKGIngest } from "../hooks";
import {
  useVCFilterSchemaQuery,
  useVCSearchQuery,
} from "../hooks/searchQueries";
import {
  convertToCSV,
  downloadCSV,
  extractContacts,
} from "../utils/exportUtils";
import { FloatingChatWidget } from "../components/BKGAgent";
import AddVCModal from "../../../components/Modals/Autodb/AddVCModal";
import ExportDropdown from "../components/ExportDropdown";
import {
  extractValue,
  toCommaList,
  formatLocation,
  formatCurrency,
  formatSingleDeal,
  formatExits,
  formatCheckSize,
  formatTotalFundSize,
  formatInternalCapabilities,
} from "../utils/ventureCapital";

const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) {
      return false;
    }
  }

  return true;
};

const NO_RESULTS: any[] = [];

const VentureCapitalSearch: React.FC = () => {
  const { workspace, isEditor } = useWorkspaceContext();
  const { workspace_id } = useParams() as { workspace_id: string };
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [error, setError] = useState<string | null>(null);

  const itemsPerPage = 50;
  const urlFilterEncoded = searchParams.get("f") || "";
  const initialFilterDoc = useMemo(
    () => decodeFilterDoc(urlFilterEncoded) || {},
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only seed on first mount
    [],
  );
  const [queryParams, setQueryParams] = useState<any>({
    skip: 0,
    limit: itemsPerPage,
    venture_capital_filter: initialFilterDoc,
  });

  const [filterValues, setFilterValues] = useState<FilterValues>(() =>
    filterValuesFromDoc(initialFilterDoc),
  );

  const lastOwnEncodedRef = useRef<string>(urlFilterEncoded);
  const [filterMountKey, setFilterMountKey] = useState<number>(0);

  useEffect(() => {
    if (urlFilterEncoded !== lastOwnEncodedRef.current) {
      lastOwnEncodedRef.current = urlFilterEncoded;
      const doc = decodeFilterDoc(urlFilterEncoded) || {};
      setQueryParams((prev: any) => ({
        ...(prev || { skip: 0, limit: itemsPerPage }),
        skip: 0,
        venture_capital_filter: doc,
      }));
      setFilterValues(filterValuesFromDoc(doc));
      setFilterMountKey((k) => k + 1);
    }
  }, [urlFilterEncoded]);

  // Both start on mount: the initial filter comes from ?f=, not the schema.
  const schemaQuery = useVCFilterSchemaQuery(workspace_id, !!workspace);
  const filterParams: FilterInterface | undefined = schemaQuery.data;
  const searchQuery = useVCSearchQuery(workspace_id, queryParams, !!workspace);
  // During a refetch the previous page stays on screen, so pagination reads
  // from the query that page answers, not from queryParams.
  const shownQuery = searchQuery.data?.query ?? queryParams;
  const results = searchQuery.data?.page.items ?? NO_RESULTS;
  const totalResults = searchQuery.data?.page.total ?? 0;
  // This endpoint doesn't return prev/next, so derive them.
  const prevPage =
    shownQuery.skip > 0
      ? Math.max(0, shownQuery.skip - shownQuery.limit)
      : undefined;
  const nextPage =
    shownQuery.skip + shownQuery.limit < totalResults
      ? shownQuery.skip + shownQuery.limit
      : undefined;
  const resultsLoading = searchQuery.isFetching;
  const resultsLoaded = !searchQuery.isPending;
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [addVCModalOpen, setAddVCModalOpen] = useState<boolean>(false);
  const [vcFirmOptions, setVCFirmOptions] = useState<
    Array<{ id: string; name: string }>
  >([]);

  const BKGVCIndexAPI = useBKGVCIndex();
  const BKGVCExportAPI = useBKGVCExport();
  const BKGIngestAPI = useBKGIngest();

  const onFilterChange = useCallback(
    (nextValues: FilterValues) => {
      setFilterValues(nextValues);
      const doc =
        (filterParams && buildFilterDoc(filterParams, nextValues)) || {};
      const encoded = encodeFilterDoc(doc);
      lastOwnEncodedRef.current = encoded;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (encoded) next.set("f", encoded);
          else next.delete("f");
          return next;
        },
        { replace: true },
      );

      setQueryParams((prev: any) => {
        const currentFilter = prev?.venture_capital_filter || {};
        if (deepEqual(doc, currentFilter)) {
          return prev;
        }
        return {
          ...prev,
          skip: 0,
          venture_capital_filter: doc,
        };
      });
    },
    [filterParams, setSearchParams],
  );

  const onChangePage = (newSkip: number) => {
    setQueryParams((prev: any) => ({
      ...prev,
      skip: Math.max(newSkip, 0),
    }));
  };

  const getVCHref = (documentId: string): string => {
    const parts = documentId.split("/").filter((part) => part);
    const firmId = parts[parts.length - 1] ?? documentId;
    return `/workspace/${workspace_id}/bkg/venture-capital/${firmId}`;
  };

  const handleFirmClick = (documentId?: string) => {
    if (!documentId || !workspace_id) return;

    // Extract the firm identifier from the full document ID
    // Example: "workspace-id/venture_capital/nordic_capital/" -> "nordic_capital"
    let firmId = documentId;
    if (documentId.includes("/")) {
      const parts = documentId.split("/").filter((part) => part); // Remove empty strings from trailing slashes
      firmId = parts[parts.length - 1]; // Get the last non-empty part
    }

    navigate(`/workspace/${workspace_id}/bkg/venture-capital/${firmId}`, {
      state: { documentId },
    });
  };

  // Preload VC firm names to power autocomplete suggestions in the add modal
  useEffect(() => {
    const fetchVCFirmOptions = async () => {
      if (!workspace_id) return;

      try {
        const response = await BKGVCIndexAPI(workspace_id, {
          limit: 200,
          orderBy: "name",
          orderDir: "asc",
        });

        const optionMap = new Map<string, { id: string; name: string }>();

        (response.items || []).forEach((item: any) => {
          const rawName = item?.value?.name?.value ?? item?.value?.name;
          const id = typeof item?._id === "string" ? item._id : undefined;

          let resolvedName: string | null = null;
          if (typeof rawName === "string") {
            resolvedName = rawName;
          } else if (
            rawName &&
            typeof rawName === "object" &&
            "value" in rawName
          ) {
            resolvedName =
              typeof rawName.value === "string" ? rawName.value : null;
          }

          if (id && resolvedName) {
            optionMap.set(id, { id, name: resolvedName });
          }
        });

        setVCFirmOptions(Array.from(optionMap.values()));
      } catch (error) {
        console.error("Failed to fetch venture capital firm options:", error);
      }
    };

    if (workspace) {
      fetchVCFirmOptions();
    }
  }, [workspace, workspace_id, BKGVCIndexAPI]);

  const onAddVCFirm = async (data: Record<string, any>) => {
    return await BKGIngestAPI(workspace_id, data);
  };

  const handleExportVCs = async () => {
    if (!workspace_id) return;

    setIsExporting(true);
    setError(null);
    try {
      const filterDoc = queryParams.venture_capital_filter || {};
      const exportResponse = await BKGVCExportAPI(workspace_id, {
        venture_capital_filter:
          filterDoc && Object.keys(filterDoc).length ? filterDoc : undefined,
      });

      const rows = (exportResponse.results || []).map((item: any) => {
        const value = item?.value ?? {};
        const exits = formatExits(value?.notable_biotech_exits?.value);
        const latestDeal =
          formatSingleDeal(value?.latest_biotech_deal) || "Not available";
        const checkSize = formatCheckSize(
          value?.check_size_usd?.value || value?.check_size_usd,
        );
        const fundSize = formatTotalFundSize(
          value?.active_funds?.value || value?.active_funds,
        );
        const internalCaps = formatInternalCapabilities(
          value?.internal_dev_capabilities,
        );
        return {
          "Firm Name": extractValue(value?.name) || "Not available",
          Website: extractValue(value?.webpage) || "",
          Location: formatLocation(value?.headquarters?.value || {}),
          "Focus Sectors": toCommaList(value?.focus_sectors),
          "Stage Focus": toCommaList(value?.stage_focus),
          "AUM (USD)": extractValue(value?.aum_usd)
            ? formatCurrency(extractValue(value?.aum_usd))
            : "",
          "Fund Size (USD)": fundSize,
          "Check Size (USD)": checkSize,
          "Internal Capabilities": internalCaps,
          "Latest Biotech Deal": latestDeal,
          "Notable Biotech Exits": exits.join("; "),
        };
      });

      const csv = convertToCSV(rows);
      if (!csv) return;

      downloadCSV(csv, "venture_capital_firms.csv");
    } catch (err) {
      console.error("Failed to export VC data:", err);
      setError("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportContacts = async () => {
    if (!workspace_id) return;

    setIsExporting(true);
    setError(null);
    try {
      const filterDoc = queryParams.venture_capital_filter || {};
      const exportResponse = await BKGVCExportAPI(workspace_id, {
        venture_capital_filter:
          filterDoc && Object.keys(filterDoc).length ? filterDoc : undefined,
      });

      const rows = extractContacts(exportResponse.results || [], {
        entityNameColumn: "Firm Name",
        contactNameColumn: "Contact Name",
        contactTitleColumn: "Contact Title",
        contactEmailColumn: "Contact Email",
        getEntityName: (vc: any) => extractValue(vc?.value?.name),
        getEntityId: (vc: any) => vc?._id,
        getContactName: (person: any) =>
          extractValue(person?.value?.name) || extractValue(person?.name),
        getContactTitle: (person: any) =>
          extractValue(person?.value?.role) || extractValue(person?.role),
        getContactEmail: (person: any) =>
          extractValue(person?.value?.email) || extractValue(person?.email),
        sources: [
          {
            getContacts: (vc: any) =>
              vc?.value?.lead_partners?.value?.value ||
              vc?.value?.lead_partners?.value ||
              vc?.value?.lead_partners ||
              [],
            getPerson: (entry: any) =>
              entry?.value?.person?.value ||
              entry?.value?.person ||
              entry?.person?.value ||
              entry?.person ||
              entry,
          },
        ],
      });

      const csv = convertToCSV(rows);
      if (!csv) return;

      downloadCSV(csv, "venture_capital_contacts.csv");
    } catch (err) {
      console.error("Failed to export VC contacts:", err);
      setError("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const staleResultsSx = {
    opacity: resultsLoading ? 0.55 : 1,
    transition: "opacity 150ms ease",
  };

  return (
    <Page sx={RAIL_PAGE_SX}>
      <PageHeader>
        <Typography level="h1">Venture Capital</Typography>
        <Stack direction="row" spacing={2}>
          {isEditor && (
            <Button
              startDecorator={<AccountBalanceIcon />}
              variant="solid"
              color="primary"
              onClick={() => setAddVCModalOpen(true)}
            >
              Add VC Firm
            </Button>
          )}
          <ExportDropdown
            loading={isExporting}
            items={[
              { label: "Export VCs", onClick: handleExportVCs },
              { label: "Export Contacts", onClick: handleExportContacts },
            ]}
          />
        </Stack>
      </PageHeader>

      <Stack spacing={3}>
        {schemaQuery.isError && (
          <Alert color="danger" variant="soft">
            Failed to load venture capital filters. Please try again.
          </Alert>
        )}
        {searchQuery.isError && (
          <Alert color="danger" variant="soft">
            Failed to load venture capital results. Please try again.
          </Alert>
        )}
        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        <FilterRailLayout
          schema={filterParams}
          schemaLoading={schemaQuery.isPending}
          value={filterValues}
          onChange={onFilterChange}
          filterKey={filterMountKey}
          primaryFacets={VC_PRIMARY_FACETS}
          resultCount={totalResults}
          resultsLoading={resultsLoading}
        >
          <Card>
            <CardContent>
              {!resultsLoaded ? (
                <Loading />
              ) : (
                <Stack spacing={2} aria-busy={resultsLoading}>
                  <Box>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography level="h3">Results</Typography>
                      <Typography level="body-sm" color="neutral">
                        {totalResults} result{totalResults === 1 ? "" : "s"}
                      </Typography>
                    </Stack>
                    <ActiveFilters
                      schema={filterParams}
                      values={filterValues}
                      onChange={onFilterChange}
                      sx={{ mt: 1 }}
                    />
                    {/* Fixed-height slot so the bar appearing doesn't shift the table. */}
                    <Box sx={{ height: 4, mt: 1 }}>
                      {resultsLoading && (
                        <LinearProgress
                          thickness={4}
                          aria-label="Updating results"
                        />
                      )}
                    </Box>
                  </Box>

                  {results.length === 0 ? (
                    <Sheet
                      variant="soft"
                      sx={{ p: 4, textAlign: "center", ...staleResultsSx }}
                    >
                      <Typography level="body-md">
                        No venture capital firms found. Try adjusting your
                        filters.
                      </Typography>
                    </Sheet>
                  ) : (
                    <Box
                      sx={{
                        ...staleResultsSx,
                        width: "100%",
                        overflowX: "auto",
                        "&::-webkit-scrollbar": {
                          height: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#f1f1f1",
                          borderRadius: "10px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#888",
                          borderRadius: "10px",
                          "&:hover": {
                            backgroundColor: "#555",
                          },
                        },
                      }}
                    >
                      <Table
                        borderAxis="bothBetween"
                        size="sm"
                        stickyHeader
                        sx={{
                          minWidth: "1600px",
                          "& td, & th": {
                            overflow: "hidden",
                            wordWrap: "break-word",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            verticalAlign: "top",
                            padding: "8px",
                          },
                          "& td:first-of-type, & th:first-of-type": {
                            // Firm
                            width: "150px",
                            minWidth: "150px",
                          },
                          "& td:nth-of-type(2), & th:nth-of-type(2)": {
                            // Focus Sectors
                            width: "140px",
                            minWidth: "140px",
                          },
                          "& td:nth-of-type(3), & th:nth-of-type(3)": {
                            // Stage Focus
                            width: "120px",
                            minWidth: "120px",
                          },
                          "& td:nth-of-type(4), & th:nth-of-type(4)": {
                            // AUM
                            width: "110px",
                            minWidth: "110px",
                          },
                          "& td:nth-of-type(5), & th:nth-of-type(5)": {
                            // Fund Size
                            width: "110px",
                            minWidth: "110px",
                          },
                          "& td:nth-of-type(6), & th:nth-of-type(6)": {
                            // Check Size
                            width: "130px",
                            minWidth: "130px",
                          },
                          "& td:nth-of-type(7), & th:nth-of-type(7)": {
                            // Internal Capabilities
                            width: "180px",
                            minWidth: "180px",
                          },
                          "& td:nth-of-type(8), & th:nth-of-type(8)": {
                            // Location
                            width: "130px",
                            minWidth: "130px",
                          },
                          "& td:nth-of-type(9), & th:nth-of-type(9)": {
                            // Latest Deal
                            width: "180px",
                            minWidth: "180px",
                          },
                          "& td:nth-of-type(10), & th:nth-of-type(10)": {
                            // Notable Exits
                            width: "200px",
                            minWidth: "200px",
                          },
                          "& td:nth-of-type(11), & th:nth-of-type(11)": {
                            // Website
                            width: "80px",
                            minWidth: "80px",
                          },
                        }}
                      >
                        <thead>
                          <tr>
                            <th>Firm</th>
                            <th>Focus Sectors</th>
                            <th>Stage Focus</th>
                            <th>AUM</th>
                            <th>Fund Size (USD)</th>
                            <th>Check Size (USD)</th>
                            <th>Internal Capabilities</th>
                            <th>Location</th>
                            <th>Latest Biotech Deal</th>
                            <th>Notable Biotech Exits</th>
                            <th>Website</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.map((result, idx) => {
                            const value = result?.value ?? {};
                            const exits = formatExits(
                              value?.notable_biotech_exits?.value,
                            );
                            const latestDeal = formatSingleDeal(
                              value?.latest_biotech_deal,
                            );
                            const firmNameRaw = extractValue(value?.name);
                            const firmName =
                              typeof firmNameRaw === "string"
                                ? firmNameRaw
                                : firmNameRaw
                                  ? String(firmNameRaw)
                                  : "Not available";
                            const rawDocumentId =
                              result?._id ??
                              result?.document_id ??
                              value?.document_id ??
                              value?.id;
                            const firmDocumentId =
                              typeof rawDocumentId === "string"
                                ? rawDocumentId
                                : undefined;
                            const aumValueRaw = extractValue(value?.aum_usd);
                            const aumValue =
                              typeof aumValueRaw === "number"
                                ? aumValueRaw
                                : typeof aumValueRaw === "string" &&
                                    !isNaN(Number(aumValueRaw))
                                  ? Number(aumValueRaw)
                                  : undefined;
                            const checkSize = formatCheckSize(
                              value?.check_size_usd?.value ||
                                value?.check_size_usd,
                            );
                            const fundSize = formatTotalFundSize(
                              value?.active_funds?.value || value?.active_funds,
                            );
                            const internalCaps = formatInternalCapabilities(
                              value?.internal_dev_capabilities,
                            );
                            return (
                              <tr key={result?._id ?? idx}>
                                <td>
                                  {firmDocumentId ? (
                                    <JoyLink
                                      href={getVCHref(firmDocumentId)}
                                      component="a"
                                      onClick={(e) => {
                                        if (
                                          e.ctrlKey ||
                                          e.metaKey ||
                                          e.shiftKey ||
                                          e.button === 1
                                        )
                                          return;
                                        e.preventDefault();
                                        handleFirmClick(firmDocumentId);
                                      }}
                                      sx={{
                                        fontWeight: 600,
                                        textAlign: "left",
                                      }}
                                    >
                                      {firmName}
                                    </JoyLink>
                                  ) : (
                                    <Typography level="title-sm">
                                      {firmName}
                                    </Typography>
                                  )}
                                </td>
                                <td>{toCommaList(value?.focus_sectors)}</td>
                                <td>{toCommaList(value?.stage_focus)}</td>
                                <td>
                                  {aumValue
                                    ? formatCurrency(aumValue)
                                    : "Not available"}
                                </td>
                                <td>{fundSize}</td>
                                <td>{checkSize}</td>
                                <td>{internalCaps}</td>
                                <td>
                                  {formatLocation(
                                    value?.headquarters?.value || {},
                                  )}
                                </td>
                                <td>{latestDeal || "Not available"}</td>
                                <td>
                                  {exits.length > 0
                                    ? exits.slice(0, 2).join("; ") +
                                      (exits.length > 2
                                        ? ` (+${exits.length - 2} more)`
                                        : "")
                                    : "Not available"}
                                </td>
                                <td>
                                  {(() => {
                                    const webpageUrl = extractValue(
                                      value?.webpage,
                                    );
                                    return webpageUrl ? (
                                      <JoyLink
                                        href={webpageUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        Visit
                                      </JoyLink>
                                    ) : (
                                      <Typography
                                        level="body-sm"
                                        color="neutral"
                                      >
                                        Not available
                                      </Typography>
                                    );
                                  })()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Box>
                  )}

                  {totalResults > 0 && (
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Typography level="body-sm" color="neutral">
                        Showing {Math.min(shownQuery.skip + 1, totalResults)}-
                        {Math.min(shownQuery.skip + itemsPerPage, totalResults)}{" "}
                        of {totalResults}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          size="sm"
                          disabled={prevPage === undefined}
                          onClick={() =>
                            prevPage !== undefined && onChangePage(prevPage)
                          }
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outlined"
                          size="sm"
                          disabled={nextPage === undefined}
                          onClick={() =>
                            nextPage !== undefined && onChangePage(nextPage)
                          }
                        >
                          Next
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </FilterRailLayout>
      </Stack>

      <AddVCModal
        open={addVCModalOpen}
        onClose={() => setAddVCModalOpen(false)}
        onAdd={onAddVCFirm}
        vcFirmOptions={vcFirmOptions}
        workspaceId={workspace_id}
      />
      <FloatingChatWidget workspaceId={workspace_id} />
    </Page>
  );
};

export default VentureCapitalSearch;
